// Resolves database-stored image paths to actual bundled asset URLs.
// Images saved as "/src/assets/cat-animals.jpg" or just "cat-animals.jpg" are
// matched to the imported asset. External URLs (http/https) pass through.

const modules = import.meta.glob("/src/assets/*.{jpg,png,jpeg,webp}", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

const byFilename: Record<string, string> = {};
for (const [path, url] of Object.entries(modules)) {
  const file = path.split("/").pop();
  if (file) byFilename[file] = url;
}

// Safety gate: do not blanket-block the article-images bucket. That made every
// newly generated cover invisible and forced static fallbacks in production.
// Block only explicitly unsafe legacy references.
export function isRevokedGeneratedArticleImage(input?: string | null): boolean {
  if (!input) return false;
  return /jo[-_\s]?louise|lingerie|nude|bikini|underwear|glamour|model|portrait|selfie/i.test(
    input,
  );
}

const UNSAFE_IMAGE_TEXT_RX =
  /\b(bra|bras|lingerie|underwear|nude|nudes|naked|bikini|swimsuit|glamour|bride|wedding|cleavage|shirtless|panty|panties|sexy|erotic|topless|boudoir|thong|onlyfans|porn|pornography|breast|breasts|nipple|nipples|buttocks)\b/i;

export function isUnsafeImageReference(input?: string | null, context?: string | null): boolean {
  const hay = `${input ?? ""} ${context ?? ""}`;
  return isRevokedGeneratedArticleImage(input) || UNSAFE_IMAGE_TEXT_RX.test(hay);
}

const FALLBACK_POOLS = {
  collection: [
    "cat-animals.jpg",
    "cat-ocean.jpg",
    "cat-nature.jpg",
    "cat-space.jpg",
    "cat-mystery.jpg",
    "featured.jpg",
  ],
  category: [
    "cat-animals.jpg",
    "cat-ocean.jpg",
    "cat-nature.jpg",
    "cat-space.jpg",
    "cat-body.jpg",
    "cat-facts.jpg",
  ],
  quiz: [
    "thumb-mantis-color.jpg",
    "thumb-brain-greedy.jpg",
    "thumb-tardigrade-space.jpg",
    "thumb-venus-day.jpg",
  ],
  discovery: ["featured.jpg", "hero.jpg", "a1.jpg", "o1.jpg", "n1.jpg", "s1.jpg"],
} as const;

type FallbackKind = keyof typeof FALLBACK_POOLS;

export function resolveImage(input?: string | null): string {
  if (!input) return "";
  if (isUnsafeImageReference(input)) return "";
  if (/^https?:\/\//i.test(input)) {
    // Stored CDN URLs are display derivatives, not originals. Unwrap them so
    // an onError fallback can reach the actual file if the resizing CDN fails.
    let original = input;
    for (let depth = 0; depth < 3; depth++) {
      if (!/^https:\/\/images\.weserv\.nl\/\?/i.test(original)) break;
      try {
        const source = new URL(original).searchParams.get("url");
        if (!source) break;
        const candidate = /^https?:\/\//i.test(source) ? source : `https://${source}`;
        const parsed = new URL(candidate);
        if (!/^https?:$/.test(parsed.protocol) || isUnsafeImageReference(candidate)) return "";
        original = candidate;
      } catch { break; }
    }
    return original;
  }
  const file = input.split("/").pop() ?? input;
  return byFilename[file] ?? input;
}

function hashSeed(seed: string) {
  return seed.split("").reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) >>> 0, 0);
}

export function resolveImageWithFallback(
  input: string | null | undefined,
  kind: FallbackKind,
  seed: string = kind,
): string {
  const resolved = resolveImage(input);
  if (resolved) return resolved;
  const pool = FALLBACK_POOLS[kind];
  const file = pool[hashSeed(seed) % pool.length];
  return byFilename[file] ?? "";
}

const SITE_ORIGIN = "https://smartasaurus.com";

/**
 * Returns an absolute, publicly fetchable URL for social/SEO crawlers.
 * Resolves bundled asset paths through resolveImage() then prefixes the origin.
 */
export function absoluteImage(input?: string | null): string | undefined {
  if (!input) return undefined;
  const resolved = resolveImage(input);
  if (!resolved) return undefined;
  if (/^https?:\/\//i.test(resolved)) return resolved;
  return `${SITE_ORIGIN}${resolved.startsWith("/") ? "" : "/"}${resolved}`;
}

/**
 * Caps remote photo weight. Wikimedia serves multi-megabyte originals; every
 * one of those files also exists as an on-demand generated thumbnail, so we
 * request the width the layout actually paints instead of the raw upload.
 * Anything we cannot resize is returned untouched.
 */
export function sizedImage(input?: string | null, width = 800): string {
  const url = resolveImage(input);
  if (!url || !/^https?:\/\//i.test(url)) return url;
  // Some stored image URLs are already resizing-CDN links with a fixed width.
  // Re-size them to the requested width instead of shipping 1280px into cards.
  if (/^https:\/\/images\.weserv\.nl\/\?/i.test(url)) {
    try {
      const u = new URL(url);
      u.searchParams.set("w", String(Math.max(120, Math.min(1920, Math.round(width)))));
      if (!u.searchParams.has("output")) u.searchParams.set("output", "webp");
      return u.toString();
    } catch {
      return url;
    }
  }
  // Signed storage transform parameters are currently ignored by the storage
  // CDN for legacy PNGs. Route display copies through a resizing CDN instead
  // of transferring the 1–3 MB originals into small cards.
  if (
    /ryzjllbwkhtalcwanshh\.supabase\.co\/storage\/v1\/(?:render\/image|object)\/sign\//i.test(url)
  ) {
    const proxyWidth = Math.max(120, Math.min(1920, Math.round(width)));
    return `https://images.weserv.nl/?url=${encodeURIComponent(url)}&w=${proxyWidth}&q=68&output=webp`;
  }
  // Our own article images are stored as multi-megabyte PNGs. The storage
  // layer can render a right-sized WebP copy from the same signed URL.
  if (/\/storage\/v1\/object\/(sign|public)\//.test(url)) {
    if (/\/render\/image\//.test(url)) return url;
    const rendered = url.replace("/storage/v1/object/", "/storage/v1/render/image/");
    const sep = rendered.includes("?") ? "&" : "?";
    return `${rendered}${sep}width=${width}&quality=70`;
  }

  if (!/upload\.wikimedia\.org\/wikipedia\//i.test(url)) return url;
  if (/\.svg(\?|$)/i.test(url)) return url;

  // Wikimedia only serves a fixed set of thumbnail widths; anything else
  // returns HTTP 400. Snap to the closest supported size.
  const ALLOWED = [120, 250, 330, 500, 1280, 1920];
  const w = ALLOWED.reduce(
    (best, cur) => (Math.abs(cur - width) < Math.abs(best - width) ? cur : best),
    ALLOWED[0],
  );

  // Wikimedia has no browser cache TTL and only serves JPEG/PNG. Route the
  // right-sized thumbnail through the same resizing CDN we already use so it
  // arrives as a cacheable WebP.
  const asWebp = (u: string) =>
    `https://images.weserv.nl/?url=${encodeURIComponent(u.replace(/^https?:\/\//, ""))}&w=${w}&q=72&output=webp`;

  // Already a thumbnail: just lower the requested width.
  const thumb = url.match(/^(.*\/thumb\/.+\/)(\d+)px-(.+)$/i);
  if (thumb) {
    const current = Number(thumb[2]);
    if (Number.isFinite(current) && current <= w) return asWebp(url);
    return asWebp(`${thumb[1]}${w}px-${thumb[3]}`);
  }

  // Original upload: /wikipedia/commons/a/a3/Name.jpg -> /thumb/a/a3/Name.jpg/800px-Name.jpg
  // Tracking query strings (?utm_source=...) are dropped before matching.
  const orig = url.match(
    /^(https?:\/\/upload\.wikimedia\.org\/wikipedia\/[^/]+\/)([0-9a-f])\/([0-9a-f]{2})\/([^/?#]+\.(?:jpe?g|png))(?:[?#].*)?$/i,
  );
  if (!orig) return asWebp(url);
  const [, base, d1, d2, file] = orig;
  return asWebp(`${base}thumb/${d1}/${d2}/${file}/${w}px-${file}`);
}
